import type { ReferralCode } from '@polaris/core/services'
import { useQueryClient } from '@tanstack/react-query'
import { Check, Loader2, X } from 'lucide-react'
import { motion } from 'motion/react'
import Image from 'next/image'
import { type ChangeEvent, useCallback, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'
import { Button } from '@/src/components/ui/button'
import { CircleExclamationIcon } from '@/src/components/ui/icons/circle-exclamation-icon'
import { CircleSlashIcon } from '@/src/components/ui/icons/circle-slash-icon'
import { Input } from '@/src/components/ui/input'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import {
  useCanCreateCode,
  useGenerateReferralCode,
} from '@/src/queries/hooks/referralcodes'
import { getReferrerProfileQuery } from '@/src/queries/keys/referralcodes'

// Maximum allowed characters for referral code input
// If user tries to type more, an error message is displayed and input is limited to this length
const MAX_REFERRAL_CODE_LENGTH = 15

interface CreateReferralCodeProps {
  onSuccess?: (referralCode: ReferralCode) => void
}

export const CreateReferralCode = ({ onSuccess }: CreateReferralCodeProps) => {
  const { ampli } = useAmplitudeAnalytics()
  const [referralName, setReferralName] = useState('')
  const [completed, setCompleted] = useState(false)
  const [_, setGeneratedCode] = useState<string | null>(null)
  const [isSimulatingCheck, setIsSimulatingCheck] = useState(false)
  const [characterLimitError, setCharacterLimitError] = useState(false)
  const [showValidationError, setShowValidationError] = useState(false)
  const [showMaxCharError, setShowMaxCharError] = useState(false)
  const queryClient = useQueryClient()

  // Debounce the referral name to avoid too many API calls
  const [debouncedReferralName] = useDebounce(referralName, 500)

  // Check if the referral code can be created
  const {
    data: canCreateData,
    isLoading: isCheckingAvailability,
    error: checkError,
    isFetched: isFetchedAvailability,
  } = useCanCreateCode(
    debouncedReferralName,
    Boolean(debouncedReferralName?.trim()),
  )

  // Generate referral code mutation
  const generateCodeMutation = useGenerateReferralCode()

  // Simulate a 300ms delay for "Checking availability" after debounced input changes
  useEffect(() => {
    if (debouncedReferralName?.trim()) {
      setIsSimulatingCheck(true)
      const timer = setTimeout(() => {
        setIsSimulatingCheck(false)
      }, 500)

      return () => clearTimeout(timer)
    }

    setIsSimulatingCheck(false)
  }, [debouncedReferralName])

  // Auto-hide character limit error after 3 seconds of no typing
  useEffect(() => {
    if (characterLimitError) {
      const timer = setTimeout(() => {
        setCharacterLimitError(false)
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [characterLimitError])

  // Show frontend validation errors - 400ms timeout for <3 chars only
  useEffect(() => {
    if (referralName.trim().length > 0 && referralName.trim().length < 3) {
      // Show after 400ms timeout for <3 characters
      const timer = setTimeout(() => {
        setShowValidationError(true)
      }, 400)

      return () => clearTimeout(timer)
    }
    setShowValidationError(false)
  }, [referralName])

  const onInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (completed) return // Don't allow changes when completed

      const newValue = event.target.value

      // Check if user is trying to exceed maximum characters
      if (newValue.length > MAX_REFERRAL_CODE_LENGTH) {
        // Show the max character error when user tries to type more than 15
        setShowMaxCharError(true)
        // Limit the input to maximum characters
        setReferralName(newValue.slice(0, MAX_REFERRAL_CODE_LENGTH))

        // Hide the error after 2 seconds
        setTimeout(() => {
          setShowMaxCharError(false)
        }, 2000)

        return
      }
      setReferralName(newValue)
      setShowMaxCharError(false)

      // Reset character limit error if within limit
      if (characterLimitError) {
        setCharacterLimitError(false)
      }

      // Reset validation error when user starts typing
      if (showValidationError) {
        setShowValidationError(false)
      }
      // Reset any generation errors when user changes input
      if (generateCodeMutation.error) {
        generateCodeMutation.reset()
      }
    },
    [completed, generateCodeMutation, characterLimitError, showValidationError],
  )

  const onConfirmLink = useCallback(() => {
    if (!referralName?.trim()) {
      console.error(
        'Referral name is required, cannot request referral code generation',
      )
      return
    }

    generateCodeMutation.mutate(
      { username: referralName.trim() },
      {
        onSuccess: (data) => {
          setGeneratedCode(data.referral_code)
          setReferralName('') // Clear the input
          setCompleted(true)
          onSuccess?.(data)

          if (ampli) {
            ampli.referralCodeGenerated({
              referralCode: data.referral_code,
            })
          }

          queryClient.invalidateQueries(getReferrerProfileQuery)
        },
        // Error handling is automatic through React Query
      },
    )
  }, [referralName, generateCodeMutation, queryClient, ampli, onSuccess])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className='flex flex-col'
    >
      <h5 className='text-5xl font-extralight font-serif text-center mb-2'>
        {completed ? "You're all set" : 'Create your link'}
      </h5>
      <p className='text-center text-secondary text-base mb-10'>
        {completed
          ? 'Spread the word and track your referrals here.'
          : 'Once created, this cannot be changed later. If you trade over $1K, future perks may be tied to early activity.'}
      </p>

      <Image
        src='https://s3.polaris.app/illustrations/referral-birds.png'
        className='mx-auto mb-10 flex-shrink-0'
        alt='Referral birds'
        width={208}
        height={106}
        style={{
          aspectRatio: '208.24/106.00',
        }}
      />

      {!completed && (
        <div className='flex flex-col gap-2 mb-10'>
          <label
            className='text-sm leading-5 text-secondary'
            htmlFor='referralName'
          >
            Create a custom code to share
          </label>
          <Input
            id='referralName'
            className='mb-2'
            dimension='large'
            value={referralName}
            onChange={onInputChange}
            disabled={completed}
          />
          <div className='h-5 flex items-center'>
            {characterLimitError ? (
              <div className='flex gap-1.5'>
                <CircleExclamationIcon />
                <p className='text-sm leading-5 text-danger-text'>
                  {MAX_REFERRAL_CODE_LENGTH} characters max
                </p>
              </div>
            ) : (
              debouncedReferralName?.trim() && (
                <div className='flex gap-1.5'>
                  {isCheckingAvailability || isSimulatingCheck ? (
                    <>
                      <Loader2 className='size-5 text-orange-400 animate-spin' />
                      <p className='text-sm leading-5 text-secondary'>
                        Checking availability
                      </p>
                    </>
                  ) : showMaxCharError ? (
                    <>
                      <CircleSlashIcon />
                      <p className='text-sm leading-5 text-primary'>
                        15 characters max
                      </p>
                    </>
                  ) : showValidationError ? (
                    <>
                      <CircleSlashIcon />
                      <p className='text-sm leading-5 text-primary'>
                        3 characters minimum
                      </p>
                    </>
                  ) : checkError ? (
                    <>
                      <X className='size-5 text-danger-text' />
                      <p className='text-sm leading-5 text-primary'>
                        {checkError.message
                          ? checkError.message
                          : 'Unavailable'}
                      </p>
                    </>
                  ) : canCreateData ? (
                    canCreateData.can_create ? (
                      <>
                        <Check className='size-5 text-success-text' />
                        <p className='text-sm leading-5 text-primary'>
                          Available
                        </p>
                      </>
                    ) : canCreateData.suggested_code ? (
                      <>
                        <CircleSlashIcon />
                        <p className='text-sm leading-5 text-'>
                          Unavailable. How about{' '}
                          <button
                            type='button'
                            className='font-bold cursor-pointer bg-transparent border-0 p-0 inline text-input-border-focus hover:opacity-80 transition-opacity'
                            onClick={() => {
                              if (canCreateData.suggested_code) {
                                setReferralName(canCreateData.suggested_code)
                              }
                            }}
                          >
                            {canCreateData.suggested_code}?
                          </button>
                        </p>
                      </>
                    ) : (
                      <>
                        <CircleExclamationIcon />
                        <p className='text-sm leading-5 text-primary'>
                          {canCreateData.reason || 'Unavailable'}
                        </p>
                      </>
                    )
                  ) : (
                    <div />
                  )}
                </div>
              )
            )}
          </div>
          {generateCodeMutation.error && (
            <div className='mt-4 p-4 bg-background rounded-3xl border border-danger-text/14 shadow-md'>
              <div className='flex items-center gap-2 mb-1'>
                <svg
                  className='size-6 text-danger-text flex-shrink-0'
                  viewBox='0 0 24 24'
                  fill='currentColor'
                  xmlns='http://www.w3.org/2000/svg'
                  aria-hidden='true'
                >
                  <path d='M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 16h2v2h-2v-2zm0-6h2v4h-2v-4z' />
                </svg>
                <p className='text-sm font-bold text-danger-text leading-6'>
                  Uh oh! Your link wasn&apos;t created.
                </p>
              </div>
              <p className='text-sm text-white leading-5 ml-8'>
                Try again or try editing your code above.
              </p>
            </div>
          )}
        </div>
      )}

      {!completed && (
        <Button
          type='button'
          className='w-full mb-10'
          variant='primary'
          disabled={
            !referralName?.trim() ||
            isCheckingAvailability ||
            isSimulatingCheck ||
            Boolean(checkError) ||
            (canCreateData && !canCreateData.can_create) ||
            !isFetchedAvailability ||
            generateCodeMutation.isPending ||
            Boolean(generateCodeMutation.error)
          }
          onClick={onConfirmLink}
        >
          {generateCodeMutation.isPending ? (
            <div className='flex items-center gap-2'>
              <Loader2 className='size-4 animate-spin' />
              Creating...
            </div>
          ) : (
            'Create link'
          )}
        </Button>
      )}
    </motion.div>
  )
}
